import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button, Card } from '../../shared/components';
import { ApiError } from '../../utils/api';
import logo from '../../assets/logo.svg';
import './Login.css';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Unable to log in right now.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="landing-page">
      <section className="landing-hero">
        <img src={logo} alt="SAT Prep" className="landing-logo" />
        <h1 className="landing-headline">
          Free SAT Prep for Students Aiming Higher
        </h1>
        <p className="landing-subhead">
          Practice questions by domain, flashcards, and a full-length timed practice exam covering
          both Reading and Writing and Math &mdash; all in one place. Logging in also enables progress
          tracking, bookmarking, highlighting, and AI-powered explanations on the study guide.
          Create your free account to get started.
        </p>

        <Card className="auth-card">
          <h2>Log in</h2>
          <form onSubmit={handleSubmit}>
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            {error && <p className="auth-error">{error}</p>}

            <Button type="submit" disabled={submitting}>
              {submitting ? 'Logging in...' : 'Log in'}
            </Button>
          </form>
          <p className="auth-switch">
            Don&apos;t have an account? <Link to="/register">Sign up</Link>
          </p>
        </Card>

        <a href="#about-the-exam" className="landing-scroll-cue">
          <span>What&apos;s on the digital SAT, and why does it matter?</span>
          <span className="landing-scroll-arrow" aria-hidden="true">&darr;</span>
        </a>
      </section>

      <section className="landing-info" id="about-the-exam">
        <div className="landing-info-block">
          <h2>What is the digital SAT?</h2>
          <p>
            The digital SAT is developed and administered by the College Board and is used by
            colleges across the country as part of undergraduate admissions. It tests two sections:
            Reading and Writing (word choice, text structure, evidence, grammar, and effective
            expression of ideas) and Math (algebra, advanced math, problem-solving and data
            analysis, and geometry and trigonometry).
          </p>
        </div>

        <div className="landing-info-block">
          <h2>Why a little preparation goes a long way</h2>
          <p>
            The digital SAT is 98 scored questions across two sections in about 134 minutes, adaptive
            by module, and scored on a 400&ndash;1600 scale. Unlike a pass/fail licensing exam, there&apos;s
            no single cutoff to clear &mdash; every additional point on test day can open up scholarship
            money and admissions options, which makes deliberate, topic-by-topic practice worth the time.
          </p>
        </div>

        <div className="landing-info-block">
          <h2>Everything you need to get ready, in one place</h2>
          <ul className="landing-feature-list">
            <li>
              <strong>Practice questions by domain</strong> &mdash; written in the same style and
              format as the real exam, weighted to match how heavily each domain is actually tested.
            </li>
            <li>
              <strong>Flashcards</strong> &mdash; key terms and concepts across every domain, with
              known/needs-review tracking.
            </li>
            <li>
              <strong>A full-length timed practice exam</strong> &mdash; questions drawn
              proportionally by domain weight, with a raw score and section breakdown.
            </li>
            <li>
              <strong>A study guide</strong> &mdash; grammar and math reference material you can
              read, highlight, and bookmark, organized the same way the real test is.
            </li>
            <li>
              <strong>AI-powered explanations</strong> &mdash; highlight any confusing passage in the
              study guide, or review any question you missed on a quiz or practice exam, and get an
              instant, plain-English explanation of why the right answer is right &mdash; using your
              own free Gemini or Groq API key.
            </li>
            <li>
              <strong>Progress tracking</strong> &mdash; so you know which domains are actually ready
              and which still need work before test day.
            </li>
          </ul>
        </div>

        <div className="landing-info-cta">
          <p>Ready to start studying for the SAT?</p>
          <Link to="/register">
            <Button>Create your free account</Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
